// Iter-196b — Audit page: surface every payment-reminder send attempt
// (cron-fired + manual) per mission/per invoice with success status and the
// error message when applicable. CEO/Gestionnaire can re-fire a manual
// reminder for any non-paid invoice straight from the table.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ExternalLink,
  Loader2, Mail, RefreshCw, XCircle,
} from "lucide-react";
import ManualReminderDialog from "@/pages/invoices/ManualReminderDialog";
import PaymentDelayChip from "@/components/PaymentDelayChip";

const fmt = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function InvoiceRemindersAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyFailures, setOnlyFailures] = useState(false);
  const [expanded, setExpanded] = useState({}); // invoice_id -> bool
  const [reminderInvoice, setReminderInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices/reminders-audit", { params: { only_failures: onlyFailures } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement audit");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyFailures]);

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="animate-spin" size={16} /> Chargement de l'audit…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="reminders-audit-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Finance · Audit</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Audit relances factures</h1>
        <p className="text-zinc-500 mt-3 max-w-2xl text-sm">
          Historique complet des emails de relance (envoyés et échecs) — milestones automatiques et envois manuels — groupés par mission puis par facture. Cliquez sur <Mail size={11} className="inline" /> Relancer pour ré-envoyer manuellement une relance échouée.
        </p>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="audit-total-attempts">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Tentatives</div>
          <div className="text-2xl font-mono font-bold text-zinc-900 mt-1">{data.totals.attempts}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="audit-total-failures">
          <div className="text-[10px] uppercase tracking-wider text-red-600 font-mono">Échecs</div>
          <div className={`text-2xl font-mono font-bold mt-1 ${data.totals.failures > 0 ? "text-red-700" : "text-zinc-900"}`}>{data.totals.failures}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="audit-invoices-with-failures">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Factures avec échec</div>
          <div className="text-2xl font-mono font-bold text-zinc-900 mt-1">{data.totals.invoices_with_failures}</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyFailures}
            onChange={(e) => setOnlyFailures(e.target.checked)}
            data-testid="audit-only-failures"
          />
          Afficher uniquement les <span className="text-red-700 font-medium">échecs</span>
        </label>
        <button
          onClick={load}
          data-testid="audit-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-zinc-200 rounded hover:bg-zinc-50"
        >
          <RefreshCw size={12} /> Rafraîchir
        </button>
      </div>

      {data.missions.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-sm text-zinc-500 italic">
          {onlyFailures ? "Aucune relance en échec ✅" : "Aucune relance enregistrée pour le moment."}
        </div>
      ) : (
        <div className="space-y-4">
          {data.missions.map((m) => (
            <section
              key={m.mission_id || "no-mission"}
              data-testid={`audit-mission-${m.mission_id || "no-mission"}`}
              className="bg-white border border-zinc-200 rounded-lg overflow-hidden"
            >
              <header className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{m.client_name}</div>
                  <div className="text-base font-semibold text-zinc-900 truncate">{m.mission_title || "(mission inconnue)"}</div>
                </div>
                <div className="flex items-center gap-3 text-xs font-mono text-zinc-600">
                  <span>{m.invoices.length} facture{m.invoices.length > 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>{m.total_attempts} tentative{m.total_attempts > 1 ? "s" : ""}</span>
                  {m.total_failures > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-red-700 font-semibold inline-flex items-center gap-1">
                        <AlertTriangle size={11} /> {m.total_failures} échec{m.total_failures > 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </div>
              </header>
              <ul className="divide-y divide-zinc-100">
                {m.invoices.map((inv) => {
                  const open = !!expanded[inv.invoice_id];
                  const hasFails = inv.failures_total > 0;
                  const canResend = !inv.paid_at;
                  return (
                    <li key={inv.invoice_id} data-testid={`audit-invoice-${inv.invoice_id}`}>
                      <div
                        className={`px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-zinc-50 ${hasFails ? "bg-red-50/40" : ""}`}
                        onClick={() => setExpanded((s) => ({ ...s, [inv.invoice_id]: !s[inv.invoice_id] }))}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-sm font-semibold text-zinc-900 flex items-center gap-2 flex-wrap">
                            {inv.invoice_number || inv.invoice_id}
                            {inv.paid_at && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded uppercase">Payée</span>}
                            {!inv.paid_at && inv.payment_overdue && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-800 rounded uppercase">Overdue</span>}
                            {(inv.payment_reminders_paused || inv.payment_reminders_paused_milestones || inv.payment_reminders_paused_overdue) && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded uppercase">Pause</span>
                            )}
                          </div>
                          <div className="text-[11px] text-zinc-600 mt-0.5 font-mono flex items-center flex-wrap gap-2">
                            <span>Envoyée le {(inv.sent_at || "").slice(0, 10)} · échéance {inv.due_date || "—"} · {fmt(inv.amount_ttc)} {inv.currency}</span>
                            {/* Iter-196e — surface the effective payment-delay chip
                                so the CEO can compare immediately why a sister
                                invoice is overdue while another is "on time". */}
                            <PaymentDelayChip inv={inv} onChanged={load} />
                            {!inv.paid_at && inv.next_milestone_due_in_days !== null && inv.next_milestone_due_in_days !== undefined && (
                              <span>· prochaine relance dans <b>{inv.next_milestone_due_in_days}j</b> (J+{inv.next_milestone})</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 text-xs">
                          <div className="text-zinc-600 font-mono">
                            {inv.attempts_total} {inv.attempts_total === 1 ? "envoi" : "envois"}
                            {inv.failures_total > 0 && <span className="text-red-700 font-semibold ml-2">{inv.failures_total} échec{inv.failures_total > 1 ? "s" : ""}</span>}
                          </div>
                          {canResend && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setReminderInvoice(inv); }}
                              data-testid={`audit-resend-${inv.invoice_id}`}
                              title="Envoyer une relance manuellement"
                              className="inline-flex items-center gap-1 px-2 h-7 text-[11px] rounded bg-zinc-900 text-white hover:bg-zinc-800"
                            >
                              <Mail size={11} /> Relancer
                            </button>
                          )}
                          <Link
                            to={`/invoices`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-zinc-500 hover:text-zinc-900"
                            title="Aller à la liste des factures"
                          >
                            <ExternalLink size={12} />
                          </Link>
                          {open ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                        </div>
                      </div>
                      {open && (
                        <div className="px-4 pb-4 bg-zinc-50/40" data-testid={`audit-invoice-detail-${inv.invoice_id}`}>
                          {inv.reminders.length === 0 ? (
                            <div className="text-xs italic text-zinc-500 py-2">
                              Aucune relance envoyée pour cette facture. Le cron déclenchera automatiquement la prochaine milestone le moment venu.
                            </div>
                          ) : (
                            <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                            <table className="w-full text-xs mt-2">
                              <thead className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                                <tr className="text-left">
                                  <th className="py-1 pr-3">Date</th>
                                  <th className="py-1 pr-3">Type</th>
                                  <th className="py-1 pr-3">Destinataire</th>
                                  <th className="py-1 pr-3">Sujet</th>
                                  <th className="py-1 pr-3 text-right">Statut</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inv.reminders.map((r, idx) => (
                                  <tr
                                    key={`${inv.invoice_id}-${idx}`}
                                    className={`border-t border-zinc-100 ${r.success === false ? "text-red-900" : ""}`}
                                    data-testid={`audit-reminder-row-${inv.invoice_id}-${idx}`}
                                  >
                                    <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{relTime(r.sent_at)}</td>
                                    <td className="py-1.5 pr-3 font-mono">
                                      {r.kind === "overdue" ? "Overdue" : `M·J+${r.milestone || "?"}`}
                                      {r.manual && <span className="ml-1 text-[9px] px-1 py-px rounded bg-blue-100 text-blue-700">manuel</span>}
                                      {r.bulk && <span className="ml-1 text-[9px] px-1 py-px rounded bg-purple-100 text-purple-700">bulk</span>}
                                    </td>
                                    <td className="py-1.5 pr-3 truncate max-w-[180px]" title={r.to}>{r.to || "—"}</td>
                                    <td className="py-1.5 pr-3 truncate max-w-[260px]" title={r.subject}>{r.subject || "—"}</td>
                                    <td className="py-1.5 pr-3 text-right">
                                      {r.success === false ? (
                                        <span
                                          className="inline-flex items-center gap-1 font-semibold text-red-700"
                                          title={r.error || "Erreur inconnue"}
                                        >
                                          <XCircle size={11} /> Échec
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-emerald-700">
                                          <CheckCircle2 size={11} /> OK
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                          )}
                          {inv.reminders.some((r) => r.success === false && r.error) && (
                            <div className="mt-3 text-[11px] bg-red-50 border border-red-200 rounded p-2 space-y-1">
                              <div className="font-semibold text-red-900">Détail des erreurs :</div>
                              {[...new Set(inv.reminders.filter((r) => r.success === false && r.error).map((r) => r.error))].map((err, i) => (
                                <div key={i} className="text-red-800 font-mono break-words">• {err}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {/* Manual reminder dialog — reuses the same component as Invoices.jsx */}
      <ManualReminderDialog
        open={!!reminderInvoice}
        onClose={() => setReminderInvoice(null)}
        invoice={reminderInvoice}
        onSent={() => { setReminderInvoice(null); load(); }}
      />
    </div>
  );
}
